import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import { PopOut, Tooltip } from 'components';
import { FaPlus, FaRegQuestionCircle } from 'react-icons/fa';
import Options from './Options';
import OptionBlock from './OptionBlock';
import OptionTag from './OptionTag';

const defaultStyle = {
    backgroundColor: 'var(--primary-color)',
    margin: '0.5rem 0 0 0',
}

const Container = styled.div`
    background-color: ${props => props.styles.backgroundColor};
    border-radius: 5px;
    box-shadow: var(--box-shadow);
    cursor: pointer;
    margin: ${props => props.styles.margin};
    border-style: solid;
    border-width: 2px;
    border-color: ${props => props.error ? 'var(--error-color)' : 'transparent'};
    :hover {
        filter: brightness(var(--brightness));
    }
`

const Wrapper = styled.div`
    width: 100%;
    padding: 0.25rem 0.5rem;
    display: grid;
    grid-template-columns: 1fr auto;
    grid-column-gap: 0.5rem;
    align-items: center;
`

const Label = styled.div`
    font-size: 0.8rem;
    font-weight: bold;
    margin: 0 0 0.2rem 0;
`

const Column = styled.div`
    width: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`

const Row = styled.div`
    display: flex;
    align-items: center;
`

const Placeholder = styled.div`
    font-size: 0.8rem;
    padding: 0 0.25rem;
    color: ${props => props.error && 'var(--error-color)'};
`

const SelectionContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    background-color: var(--secondary-color);
    padding: 0.5rem 0.5rem 0.3rem 0.5rem;
    border-radius: 5px;
    overflow: hidden;
    box-shadow: var(--box-shadow);
    margin-bottom: 0.5rem;
    min-height: 2.5rem;
`

const TagContainer = styled.div`
    min-height: 1rem;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    overflow: hidden;
    min-height: 1.5rem;
`

const MultiSelector = props => {
    const {
        label,
        selected,
        options,
        onSelect,
        error,
        styles,
        helper
    } = props;
    const updatedStyle = Object.assign({}, defaultStyle, styles)
    const [open, setOpen] = useState(false)
    const [width, setWidth] = useState(null)
    const ref = React.createRef()
    useEffect(() => {
        setWidth(ref.current.getBoundingClientRect()['width']+5)
    }, [])

    ///////////////
    // UTILITIES //
    ///////////////
    const onSelectHandler = (tagID) => {
        let updatedTags = selected ? [...selected] : []
        !updatedTags.includes(tagID) && updatedTags.push(tagID)
        onSelect(updatedTags)
    }

    const getOptions = () => {
        let blocks = []
        // Get selected options
        if(selected){
            let optionTags = selected.map(value => <OptionTag option={options[value]} onDelete={() => removeTag(value)} style={{backgroundColor: 'var(--primary-color)'}}/>)
            const selection = <SelectionContainer>{optionTags}</SelectionContainer>
            blocks.push(selection)
        }

        // Get available options
        let optionBlocks = Object.values(options).map(option => (<OptionBlock title={option['title']} subtitle={option['subtitle']} icon={option['icon']} onSelect={() => onSelectHandler(option['value'])}/>))
        blocks.push(...optionBlocks)
        return <Options width={width} blocks={blocks}/>
    }

    const removeTag = tagID => {
        let updatedTags = [...selected]
        updatedTags.splice(updatedTags.indexOf(tagID), 1)
        onSelect(updatedTags)
    }

    const getTags = () => {
        let optionTags = selected ? selected.map(value => <OptionTag option={options[value]}/>) : null

        return optionTags
    }

    const optionBlock = getOptions()
    return (
        <Container error={error} styles={updatedStyle}>
            <PopOut trigger='leftClick' placement='bottom' element={optionBlock} onOpen={() => setOpen(true)} onClose={() => setOpen(false)}>
                <Wrapper ref={ref}>
                    <Column>
                        <Row>
                            <Label>{label}</Label>
                            {helper && 
                            <Tooltip title={helper}>
                                <div style={{display: 'flex', color: 'var(--secondary-accent-color)', fontSize: '0.8rem', margin: '0 0.3rem', cursor: 'pointer'}}><FaRegQuestionCircle/></div>
                            </Tooltip>}
                        </Row>
                        <TagContainer>
                            {getTags()}
                        </TagContainer>
                    </Column>
                    <FaPlus style={{color: 'var(--secondary-color)', fontSize: '0.9rem', fontWeight: 'bold', transform: open && 'scaleY(-1)'}}/>
                </Wrapper>
            </PopOut>
        </Container>
    )
}

export default MultiSelector;
